import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { linkitApi } from "../api";
import { StatCard, ActivityLineChart, TopBarChart, RangeToggle } from "../../components/AnalyticsCharts";

// Country ISO-2 → friendly name for the top-country breakdown.
// Kept intentionally small — anything unmatched just renders the ISO code.
const COUNTRY_NAMES = {
  IN: "India", US: "United States", GB: "United Kingdom", CA: "Canada",
  AU: "Australia", AE: "UAE", SG: "Singapore", DE: "Germany", FR: "France",
  NL: "Netherlands", ES: "Spain", IT: "Italy", JP: "Japan", KR: "South Korea",
  BR: "Brazil", MX: "Mexico", ZA: "South Africa", NZ: "New Zealand", PH: "Philippines",
  ID: "Indonesia", VN: "Vietnam", TH: "Thailand", PK: "Pakistan", BD: "Bangladesh",
  LK: "Sri Lanka", NP: "Nepal", MY: "Malaysia", XX: "Unknown",
};

const countryLabel = (iso) => COUNTRY_NAMES[iso] || (iso === "unknown" ? "Unknown" : iso || "Unknown");

function UtmTable({ rows = [] }) {
  if (!rows.length) {
    return (
      <div data-testid="linkit-chart-utm" className="rounded-2xl border border-black/10 bg-white p-4">
        <p className="mb-3 text-sm font-bold text-black/70">Top UTM campaigns</p>
        <p className="text-sm text-black/40">No campaigns tracked yet. Share links with utm_source, utm_medium, and utm_campaign query params to see them here.</p>
      </div>
    );
  }
  return (
    <div data-testid="linkit-chart-utm" className="rounded-2xl border border-black/10 bg-white p-4">
      <p className="mb-3 text-sm font-bold text-black/70">Top UTM campaigns</p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-black/50">
            <tr className="border-b border-black/5">
              <th className="py-2 pr-3">Source</th>
              <th className="py-2 pr-3">Medium</th>
              <th className="py-2 pr-3">Campaign</th>
              <th className="py-2 text-right">Events</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-black/5 last:border-none">
                <td className="py-2 pr-3 font-semibold text-black/80">{r.source || "-"}</td>
                <td className="py-2 pr-3 text-black/70">{r.medium || "-"}</td>
                <td className="py-2 pr-3 text-black/70">{r.campaign || "-"}</td>
                <td className="py-2 text-right font-semibold text-black/80">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function LinkitAnalytics() {
  const [a, setA] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    linkitApi.get(`/page/analytics?days=${days}`).then((r) => setA(r.data)).finally(() => setLoading(false));
  }, [days]);

  if (loading && !a) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/40" /></div>;
  if (!a) return null;

  // Rebrand backend `key` to something human — TopBarChart uses `nameKey`.
  const asLabel = (arr, mapFn) => (arr || []).map((r) => ({ label: mapFn ? mapFn(r.key) : r.key, count: r.count }));
  const devices = asLabel(a.devices, (k) => (k ? k[0].toUpperCase() + k.slice(1) : "Unknown"));
  const browsers = asLabel(a.browsers);
  const countries = asLabel(a.countries, countryLabel);
  const referrers = asLabel(a.referrers, (k) => (k === "direct" ? "Direct / none" : k));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Analytics</h2>
          <p className="text-xs text-white/50">Views, clicks, leads — and where your audience comes from.</p>
        </div>
        <RangeToggle value={days} onChange={setDays} testid="linkit-range" theme="dark" />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard testid="linkit-stat-views" label="Views" value={a.total_views} tint="indigo" />
        <StatCard testid="linkit-stat-clicks" label="Clicks" value={a.total_clicks} tint="emerald" />
        <StatCard testid="linkit-stat-ctr" label="CTR" value={`${a.ctr}%`} tint="amber" />
        <StatCard testid="linkit-stat-leads" label="Leads" value={a.total_leads} tint="rose" />
      </div>

      <ActivityLineChart
        series={a.series || []}
        keys={[
          { key: "views", label: "Views", color: "#6366f1" },
          { key: "clicks", label: "Clicks", color: "#10b981" },
          { key: "leads", label: "Leads", color: "#f43f5e" },
        ]}
        testid="linkit-chart-activity"
      />

      <TopBarChart
        data={a.top_blocks || []}
        nameKey="label"
        valueKey="count"
        title="Top clicked blocks"
        color="#8b5cf6"
        testid="linkit-chart-top"
      />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TopBarChart data={devices} nameKey="label" valueKey="count" title="Device breakdown" color="#0ea5e9" testid="linkit-chart-devices" />
        <TopBarChart data={browsers} nameKey="label" valueKey="count" title="Top browsers" color="#f59e0b" testid="linkit-chart-browsers" />
        <TopBarChart data={countries} nameKey="label" valueKey="count" title="Top countries" color="#10b981" testid="linkit-chart-countries" />
        <TopBarChart data={referrers} nameKey="label" valueKey="count" title="Top referrers" color="#ec4899" testid="linkit-chart-referrers" />
      </div>

      <UtmTable rows={a.utm || []} />
    </div>
  );
}
